import { useState, useCallback, useRef } from 'react';
import { generateId, logger } from '@/utils';
import type { MondayAsset, AsyncState } from '@/types';
import { mondayService } from '@/services/api';

interface UseAssetsConfig {
  initialData?: MondayAsset[];
}

export function useAssets({ initialData = [] }: UseAssetsConfig) {
  const [assets, setAssets] = useState<AsyncState<MondayAsset[]>>({
    data: initialData,
    loading: false,
    error: null,
  });

  const [selectedAsset, setSelectedAsset] = useState<MondayAsset | null>(null);

  const fetched = useRef(false);

  const fetchAssets = useCallback(async () => {
    setAssets((prev) => ({ ...prev, loading: prev.data.length === 0, error: null }));

    try {
      logger.log('[useAssets] Fetching assets from Monday.com...');
      const data = await mondayService.getAssets();
      logger.log(`[useAssets] Loaded ${data.length} assets from Monday.com`);
      setAssets({ data, loading: false, error: null });
    } catch (error) {
      logger.simpleError('[useAssets] Error fetching assets:', error);
      setAssets((prev) => ({
        ...prev,
        loading: false,
        error: (error as Error).message,
      }));
    }
  }, []);

  const createAsset = useCallback((assetData: Omit<MondayAsset, 'id' | 'createdDate' | 'lastUpdated'>) => {
    const today = new Date().toISOString().split('T')[0] ?? '';
    const newAsset: MondayAsset = {
      ...assetData,
      id: generateId('asset'),
      createdDate: today,
      lastUpdated: today,
    };
    setAssets((prev) => ({
      ...prev,
      data: [...prev.data, newAsset],
    }));
    return newAsset.id;
  }, []);

  const updateAsset = useCallback((assetId: string, updates: Partial<MondayAsset>) => {
    const today = new Date().toISOString().split('T')[0] ?? '';
    setAssets((prev) => ({
      ...prev,
      data: prev.data.map((asset) =>
        asset.id === assetId
          ? { ...asset, ...updates, lastUpdated: today }
          : asset
      ),
    }));
  }, []);

  const deleteAsset = useCallback((assetId: string) => {
    setAssets((prev) => ({
      ...prev,
      data: prev.data.filter((asset) => asset.id !== assetId),
    }));
    setSelectedAsset(null);
  }, []);

  const importCaseStudiesFromWebsite = useCallback(async () => {
    const today = new Date().toISOString().split('T')[0] ?? '';
    const websiteUrl = 'https://www.protagona.com';

    // Real case studies from protagona.com/case-studies
    const websiteCaseStudies: Omit<MondayAsset, 'id' | 'createdDate' | 'lastUpdated'>[] = [
      {
        name: 'Major Airline Application Migration and Re-Architecture',
        type: 'Case Study',
        status: 'Published',
        industry: ['Travel & Hospitality'],
        tags: ['cloud acceleration', 'migration', 'AWS', 'airline', 're-architecture'],
        description: 'How a major airline modernized their applications through migration and re-architecture on AWS.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'VP Engineering', 'Cloud Architect'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/major-airline-application-migration-and-re-architecture`,
        sourceUrl: `${websiteUrl}/case-studies/major-airline-application-migration-and-re-architecture`,
        thumbnail: '✈️',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'Scaling Human Potential: AI-Powered Growth Strategy for Conduxt',
        type: 'Case Study',
        status: 'Published',
        industry: ['Startups & Software'],
        tags: ['AI', 'growth strategy', 'cloud acceleration', 'startup'],
        description: 'How Protagona delivered an AI-powered growth strategy roadmap for Conduxt.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CEO', 'CTO', 'VP Product'],
        buyerStage: ['Awareness', 'Consideration'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/conduxt-ai-growth-strategy-roadmap`,
        sourceUrl: `${websiteUrl}/case-studies/conduxt-ai-growth-strategy-roadmap`,
        thumbnail: '🚀',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'TCM Security: Scalable Browser-Based Cyber Labs',
        type: 'Case Study',
        status: 'Published',
        industry: ['Startups & Software'],
        tags: ['cybersecurity', 'cloud acceleration', 'training', 'browser-based'],
        description: 'Redefining cybersecurity training with seamless lab access from any device.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CISO', 'VP Security', 'Training Director'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/tcm-security-scalable-browser-based-cyber-labs`,
        sourceUrl: `${websiteUrl}/case-studies/tcm-security-scalable-browser-based-cyber-labs`,
        thumbnail: '🔒',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'Reimagining Healthcare Intelligence with Generative AI',
        type: 'Case Study',
        status: 'Published',
        industry: ['Healthcare'],
        tags: ['AI', 'generative AI', 'healthcare', 'intelligence'],
        description: 'How HealthHive reimagined healthcare intelligence using generative AI solutions.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['Chief Medical Officer', 'CTO', 'VP Data'],
        buyerStage: ['Awareness', 'Consideration'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/reimagining-healthcare-intelligence-with-generative-ai`,
        sourceUrl: `${websiteUrl}/case-studies/reimagining-healthcare-intelligence-with-generative-ai`,
        thumbnail: '🏥',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'CompanyNurse: Modernized Data and Application Strategy',
        type: 'Case Study',
        status: 'Published',
        industry: ['Healthcare'],
        tags: ['data engineering', 'modernization', 'healthcare', 'growth'],
        description: 'Helping CompanyNurse prepare for future growth with modernized data and application strategy.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'VP Engineering', 'Data Architect'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/helping-companynurse-prepare-for-future-growth-with-modernized-data-and-application-strategy`,
        sourceUrl: `${websiteUrl}/case-studies/helping-companynurse-prepare-for-future-growth-with-modernized-data-and-application-strategy`,
        thumbnail: '📊',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'Pacific Dental Services: Revenue Cycle Observability',
        type: 'Case Study',
        status: 'Published',
        industry: ['Healthcare'],
        tags: ['observability', 'revenue cycle', 'healthcare', 'cloud acceleration'],
        description: 'Laying the foundation for observability in revenue cycle operations for Pacific Dental Services.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CFO', 'VP Operations', 'Director of Revenue Cycle'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/pacific-dental-services-laying-the-foundation-for-observability-in-revenue-cycle-operations`,
        sourceUrl: `${websiteUrl}/case-studies/pacific-dental-services-laying-the-foundation-for-observability-in-revenue-cycle-operations`,
        thumbnail: '🦷',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'FinTech: Raw Data to Actionable Insights with AWS',
        type: 'Case Study',
        status: 'Published',
        industry: ['Financial Services'],
        tags: ['data engineering', 'AWS Glue', 'S3', 'Quicksight', 'fintech'],
        description: 'FinTech platform provider transforms raw data into actionable insights with AWS Glue, S3, and Quicksight.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'VP Data', 'Data Engineer'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/fintech-platform-provider-transforms-raw-data-into-actionable-insights-with-aws-glue-s3-and-quicksight`,
        sourceUrl: `${websiteUrl}/case-studies/fintech-platform-provider-transforms-raw-data-into-actionable-insights-with-aws-glue-s3-and-quicksight`,
        thumbnail: '💹',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'PriceSpider: Analytics Infrastructure Scaling',
        type: 'Case Study',
        status: 'Published',
        industry: ['Financial Services'],
        tags: ['database modernization', 'cost optimization', 'analytics', 'scaling'],
        description: 'PriceSpider scales analytics infrastructure by orders of magnitude through database modernization.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'VP Engineering', 'Director of Analytics'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/database-modernization-cost-optimization-and-performance-planning----pricespider-scales-analytics-infrastructure-by-orders-of-magnitude`,
        sourceUrl: `${websiteUrl}/case-studies/database-modernization-cost-optimization-and-performance-planning----pricespider-scales-analytics-infrastructure-by-orders-of-magnitude`,
        thumbnail: '📈',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'Cenergistic: GenAI & IDP for Utility Bill Processing',
        type: 'Case Study',
        status: 'Published',
        industry: ['Financial Services'],
        tags: ['GenAI', 'IDP', 'automation', 'energy savings', 'utility'],
        description: 'Cenergistic harnesses GenAI and IDP to triple utility bill processing efficiency, accelerating energy savings.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['COO', 'VP Operations', 'Director of Automation'],
        buyerStage: ['Awareness', 'Consideration'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/cenergistic-harnesses-genai-and-idp-to-triple-utility-bill-processing-efficiency-accelerating-energy-savings`,
        sourceUrl: `${websiteUrl}/case-studies/cenergistic-harnesses-genai-and-idp-to-triple-utility-bill-processing-efficiency-accelerating-energy-savings`,
        thumbnail: '⚡',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'Litterati: AI-Powered Waste Detection and Annotation',
        type: 'Case Study',
        status: 'Published',
        industry: ['Startups & Software'],
        tags: ['AI', 'machine learning', 'waste detection', 'annotation', 'environmental'],
        description: 'Litterati partners with Protagona to enhance AI-powered waste detection and annotation capabilities.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'VP Engineering', 'ML Engineer'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/litterati-partners-with-protagona-to-enhance-ai-powered-waste-detection-and-annotation`,
        sourceUrl: `${websiteUrl}/case-studies/litterati-partners-with-protagona-to-enhance-ai-powered-waste-detection-and-annotation`,
        thumbnail: '🌍',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'AI-Powered Healthcare Benefits Platform',
        type: 'Case Study',
        status: 'Published',
        industry: ['Healthcare'],
        tags: ['AI', 'healthcare', 'benefits', 'validation', 'automation'],
        description: 'AI-powered healthcare benefits platform slashes validation time from weeks to seconds.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'VP Product', 'Director of Engineering'],
        buyerStage: ['Awareness', 'Consideration'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/ai-powered-benefits-platform`,
        sourceUrl: `${websiteUrl}/case-studies/ai-powered-benefits-platform`,
        thumbnail: '🏥',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'Vast Bank: Secure Financial Data ETL',
        type: 'Case Study',
        status: 'Published',
        industry: ['Financial Services'],
        tags: ['cloud acceleration', 'ETL', 'security', 'banking', 'data'],
        description: 'Vast Bank provides secure financial data ETL to partners using cloud acceleration.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'CISO', 'VP Data'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/vast-bank-provides-secure-financial-data-etl-to-partners`,
        sourceUrl: `${websiteUrl}/case-studies/vast-bank-provides-secure-financial-data-etl-to-partners`,
        thumbnail: '🏦',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'HappyNest: 90% Faster Income Verification with AI',
        type: 'Case Study',
        status: 'Published',
        industry: ['Financial Services'],
        tags: ['AI', 'automation', 'income verification', 'fintech'],
        description: 'HappyNest slashes income verification time by 90% through AI automation.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'VP Engineering', 'Director of Product'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/happynest-slashes-income-verification-time-by-90-through-ai-automation`,
        sourceUrl: `${websiteUrl}/case-studies/happynest-slashes-income-verification-time-by-90-through-ai-automation`,
        thumbnail: '🏠',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'HungryFriend: AI for Customer Onboarding',
        type: 'Case Study',
        status: 'Published',
        industry: ['Travel & Hospitality'],
        tags: ['AI', 'data engineering', 'onboarding', 'automation'],
        description: 'How HungryFriend used AI to reduce customer onboarding and processing times.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'VP Product', 'Director of Engineering'],
        buyerStage: ['Awareness', 'Consideration'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/accelerate-client-qualification-with-ai`,
        sourceUrl: `${websiteUrl}/case-studies/accelerate-client-qualification-with-ai`,
        thumbnail: '🍔',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'Vast Bank Crypto Platform Migration',
        type: 'Case Study',
        status: 'Published',
        industry: ['Financial Services'],
        tags: ['AWS', 'migration', 'crypto', 'modernization', 'fintech'],
        description: 'Vast Bank crypto platform migration and modernization on AWS.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'VP Engineering', 'Cloud Architect'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/aws-migration-modernization-crypto-platform`,
        sourceUrl: `${websiteUrl}/case-studies/aws-migration-modernization-crypto-platform`,
        thumbnail: '₿',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'AlgoTech: 90%+ Crypto Trading Uptime on AWS',
        type: 'Case Study',
        status: 'Published',
        industry: ['Financial Services'],
        tags: ['cloud acceleration', 'crypto', 'trading', 'uptime', 'AWS'],
        description: 'AlgoTech leverages AWS to boost crypto trading uptime by 90%+.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'VP Infrastructure', 'SRE Lead'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/algotech-global-crypto-expansion`,
        sourceUrl: `${websiteUrl}/case-studies/algotech-global-crypto-expansion`,
        thumbnail: '📈',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'C-Stream: AI-Automated Infrastructure Vulnerability Remediation',
        type: 'Case Study',
        status: 'Published',
        industry: ['Startups & Software'],
        tags: ['AI', 'automation', 'security', 'vulnerability', 'code generation'],
        description: 'How C-Stream automated remediation of infrastructure vulnerabilities using AI.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CISO', 'VP Engineering', 'Security Architect'],
        buyerStage: ['Awareness', 'Consideration'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/leveraging-ai-to-automate-code-generation-for-remediation-of-infrastructure-vulnerabilities`,
        sourceUrl: `${websiteUrl}/case-studies/leveraging-ai-to-automate-code-generation-for-remediation-of-infrastructure-vulnerabilities`,
        thumbnail: '🛡️',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'AWS Migration for Enhanced Resiliency and Security',
        type: 'Case Study',
        status: 'Published',
        industry: ['Travel & Hospitality'],
        tags: ['cloud acceleration', 'AWS', 'migration', 'security', 'resiliency'],
        description: 'Enterprise AWS migration for enhanced resiliency and security in the travel industry.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'CISO', 'Cloud Architect'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/aws-migration-for-enhanced-resiliency-and-security`,
        sourceUrl: `${websiteUrl}/case-studies/aws-migration-for-enhanced-resiliency-and-security`,
        thumbnail: '☁️',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'Subeca: Amazon Sidewalk for IoT Data Ingestion',
        type: 'Case Study',
        status: 'Published',
        industry: ['Startups & Software'],
        tags: ['data engineering', 'IoT', 'Amazon Sidewalk', 'data ingestion'],
        description: 'Subeca harnesses Amazon Sidewalk to streamline IoT data ingestion and boost processing capacity.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'VP Engineering', 'IoT Architect'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/subeca-harnesses-amazon-sidewalk`,
        sourceUrl: `${websiteUrl}/case-studies/subeca-harnesses-amazon-sidewalk`,
        thumbnail: '📡',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
      {
        name: 'HappyNest: AWS Personalize for Engagement',
        type: 'Case Study',
        status: 'Published',
        industry: ['Startups & Software'],
        tags: ['data engineering', 'AWS Personalize', 'engagement', 'revenue'],
        description: 'How HappyNest leverages AWS Personalize to boost engagement and revenue.',
        metrics: { downloads: 0, shares: 0, conversionRate: 0 },
        targetPersona: ['CTO', 'VP Product', 'Director of Data Science'],
        buyerStage: ['Consideration', 'Decision'],
        fileUrl: null,
        previewUrl: `${websiteUrl}/case-studies/how-happynest-leverages-aws-personalize-to-boost-engagement-and-revenue`,
        sourceUrl: `${websiteUrl}/case-studies/how-happynest-leverages-aws-personalize-to-boost-engagement-and-revenue`,
        thumbnail: '🏠',
        owner: 'Marketing Team',
        relatedCampaigns: [],
        source: 'website',
      },
    ];

    // Check for duplicates and only add new ones
    const existingUrls = new Set(assets.data.map((a) => a.sourceUrl).filter(Boolean));
    const newCaseStudies = websiteCaseStudies.filter(
      (cs) => !existingUrls.has(cs.sourceUrl)
    );

    if (newCaseStudies.length === 0) {
      logger.log('[useAssets] All case studies from website already imported');
      return { imported: 0, skipped: websiteCaseStudies.length };
    }

    const importedAssets: MondayAsset[] = newCaseStudies.map((cs) => ({
      ...cs,
      id: generateId('asset'),
      createdDate: today,
      lastUpdated: today,
    }));

    setAssets((prev) => ({
      ...prev,
      data: [...prev.data, ...importedAssets],
    }));

    logger.log(`[useAssets] Imported ${importedAssets.length} case studies from website`);
    return { imported: importedAssets.length, skipped: websiteCaseStudies.length - newCaseStudies.length };
  }, [assets.data]);

  return {
    // Data
    assets: assets.data,
    assetsLoading: assets.loading,
    assetsError: assets.error,

    // Selection
    selectedAsset,
    setSelectedAsset,

    // Actions
    fetchAssets,
    createAsset,
    updateAsset,
    deleteAsset,
    importCaseStudiesFromWebsite,

    // Internal (for orchestrator)
    setAssets,
    fetched,
  };
}

export type UseAssetsReturn = ReturnType<typeof useAssets>;
